import React, { useEffect, useState } from "react";
import { Button, Input, Select, Option, Tooltip } from "@mui/joy";
import BlockIcon from "@mui/icons-material/Block";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../../../fetch/fetch";
import CustomInput from "../../../etc/custominput";
import { NONE_KEY } from "./contractvarkeys";
import { API, toastOptions, errorText, Position, Stamp, mmddHHmm } from "./rosterutils";

//신규/퇴사직원 TODO 공용 — 셀 컴포넌트(기록 버튼·날짜·선택·텍스트), 필터 바, 완료 판정 함수, 저장 헬퍼
//룩은 학생용 신규/퇴원 TODO(new.tsx)를 따름: 미기록 = soft neutral 버튼 + BlockIcon, 기록 = 초록 "이름 · MM-DD HH:mm" (누르면 확인 후 해제)

export const fmtStamp = (s: Stamp | null | undefined) => (s ? `${s.doneByName || ""} · ${mmddHHmm(s.doneAt)}` : "");

//===== 저장 헬퍼 (결과 객체 반환, 토스트는 호출부에서) =====
export const saveStamp = (positionId: number, page: "onboarding" | "offboarding", key: string, value: any): Promise<any> =>
    postFetch(`${API}/todo/stamp`, { positionId, page, key, value }) as Promise<any>;
export const saveField = (id: number, field: string, value: any): Promise<any> =>
    postFetch(`${API}/positions/field`, { id, field, value }) as Promise<any>;
export const saveSexOffender = (userId: number, checked: boolean): Promise<any> =>
    postFetch(`${API}/todo/sexoffender`, { userId, checked }) as Promise<any>;

//셀 안 키 입력이 DataGrid 로 새지 않게
const stop = (e: any) => e.stopPropagation();

//===== 기록 버튼 =====
//stamp 없음 → 버튼(onSet) / 있음 → 초록 기록, 클릭 시 확인 후 onClear
export const StampButton: React.FC<{
    stamp: Stamp | null | undefined;
    label?: string;
    text?: string;                 //기록 상태 표시 텍스트 (기본 fmtStamp)
    disabled?: boolean;
    disabledReason?: string;
    clearable?: boolean;           //false 면 기록 후 클릭 불가 (직무해제 등)
    onSet: () => Promise<void>;
    onClear?: () => Promise<void>;
}> = (props) => {
    const [busy, setBusy] = useState(false);
    const run = async (fn: () => Promise<void>) => {
        try {
            setBusy(true);
            await fn();
        } finally {
            setBusy(false);
        }
    };
    if (props.stamp) {
        const btn = (
            <Button size="sm" variant="soft" color="success" loading={busy}
                sx={{ cursor: props.clearable === false ? "default" : "pointer", fontFamily: "Apple_R", fontSize: "12px" }}
                onClick={() => {
                    if (props.clearable === false || !props.onClear) return;
                    if (!window.confirm("이 기록을 지울까요?")) return;
                    run(props.onClear);
                }}>
                {props.text || fmtStamp(props.stamp)}
            </Button>
        );
        return btn;
    }
    const btn = (
        <span>
            <Button size="sm" variant="soft" color="neutral" loading={busy} disabled={props.disabled} onClick={() => run(props.onSet)}>
                {props.label ? props.label : <BlockIcon fontSize="small" />}
            </Button>
        </span>
    );
    return props.disabled && props.disabledReason ? <Tooltip title={props.disabledReason} placement="top">{btn}</Tooltip> : btn;
};

//===== 날짜 셀 (blur/Enter 커밋, 같은 값이면 요청 생략) =====
export const DateCell: React.FC<{ value: string; disabled?: boolean; naText?: string; onCommit: (v: string | null) => Promise<void> }> = (props) => {
    const [v, setV] = useState(props.value || "");
    useEffect(() => { setV(props.value || ""); }, [props.value]);
    if (props.naText) {
        return <span style={{ color: "#9ca3af", fontSize: "12px" }}>{props.naText}</span>;
    }
    const commit = async () => {
        if ((v || "") === (props.value || "")) return;
        await props.onCommit(v || null);
    };
    return (
        <Input size="sm" type="date" value={v} disabled={props.disabled} sx={{ width: "140px" }}
            onKeyDown={(e) => { stop(e); if (e.key === "Enter") commit(); }}
            onChange={(e) => setV(e.target.value)}
            onBlur={commit}
        />
    );
};

//===== 선택 셀 =====
export const SelectCell: React.FC<{
    value: string; options: { value: string; label: string }[]; disabled?: boolean; width?: number;
    placeholder?: string;          //미저장 시 회색 제안 문구
    onCommit: (v: string) => Promise<void>;
}> = (props) => {
    const [busy, setBusy] = useState(false);
    return (
        <Select size="sm" value={props.value || NONE_KEY} disabled={props.disabled || busy} placeholder={props.placeholder}
            sx={{ width: `${props.width || 110}px`, ...(props.value ? {} : { color: "#9ca3af" }) }}
            onChange={async (e: any, v: any) => {
                if (v === null) return;
                const next = v === NONE_KEY ? "" : v;
                if (next === (props.value || "")) return;
                try { setBusy(true); await props.onCommit(next); } finally { setBusy(false); }
            }}>
            <Option value={NONE_KEY}>{props.placeholder ? props.placeholder : "(선택)"}</Option>
            {props.options.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
        </Select>
    );
};

//===== 텍스트 셀 (blur/Enter 커밋) =====
export const TextCell: React.FC<{ value: string; disabled?: boolean; width?: number; onCommit: (v: string) => Promise<void> }> = (props) => {
    const [v, setV] = useState(props.value || "");
    useEffect(() => { setV(props.value || ""); }, [props.value]);
    const commit = async () => {
        if ((v || "") === (props.value || "")) return;
        await props.onCommit(v);
    };
    return (
        <Input size="sm" value={v} disabled={props.disabled} placeholder="메모" sx={{ width: `${props.width || 140}px` }}
            onKeyDown={(e) => { stop(e); if (e.key === "Enter") commit(); }}
            onChange={(e) => setV(e.target.value)}
            onBlur={commit}
        />
    );
};

//===== 필터 바: 미완료/전체 · 지점 · 이름 검색 =====
export const TodoFilterBar: React.FC<{
    status: "incomplete" | "all" | "closed"; onStatus: (v: any) => void;
    showClosed?: boolean;      //신규직원 TODO: "완료 처리" 토글 (closed 스탬프 있는 줄만)
    branch: string; onBranch: (v: string) => void; branchOptions: { english: string; korean: string }[];
    search: string; onSearch: (v: string) => void;
    right?: React.ReactNode;
}> = (props) => {
    const toggleSx = { "& .MuiToggleButton-root": { padding: "4px 10px", fontSize: "12px" } };
    return (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
            <ToggleButtonGroup color="primary" size="small" exclusive value={props.status} sx={toggleSx} onChange={(e, v) => { if (v) props.onStatus(v); }}>
                <ToggleButton value="incomplete">미완료</ToggleButton>
                <ToggleButton value="all">전체</ToggleButton>
                {props.showClosed && <ToggleButton value="closed">완료 처리</ToggleButton>}
            </ToggleButtonGroup>
            <ToggleButtonGroup color="primary" size="small" exclusive value={props.branch} sx={toggleSx} onChange={(e, v) => { if (v) props.onBranch(v); }}>
                <ToggleButton value="all">전체</ToggleButton>
                {props.branchOptions.map((b) => <ToggleButton value={b.english} key={b.english}>{b.korean}</ToggleButton>)}
            </ToggleButtonGroup>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "12px" }}>
                {props.right}
                <CustomInput value={props.search} onChange={(e: any) => props.onSearch(e.target.value)} placeholder="이름 검색" />
            </div>
        </div>
    );
};

//===== 완료 판정 =====
const insuranceNA = (p: Position) => p.employmentInsurance === "X" || p.employmentInsurance === "해당X";

//취득신고: 고보 X/해당X → 해당없음 / 같은 사람의 다른 직무에 이행일이 있으면 해당없음(겸직) / 아니면 입력
export const acqMode = (p: Position, siblings: Position[]): "na" | "naMulti" | "input" => {
    if (insuranceNA(p)) return "na";
    if (siblings.some((s) => s.onboarding && s.onboarding.acquisitionDone)) return "naMulti";
    return "input";
};

//상실신고: 고보 X/해당X → 해당없음 / 아니면 입력 (겸직이어도 입력 가능 — 8차. 완료 판정에서만 겸직은 필수 아님)
export const lossMode = (p: Position, siblings: Position[]): "na" | "naMulti" | "input" => {
    if (insuranceNA(p)) return "na";
    return "input";
};
//다른 파트가 계속 재직 중인지 (겸직 퇴사)
export const hasOtherActive = (siblings: Position[]): boolean => siblings.some((s) => s.isActive === 1 && !s.endDate);

//퇴직금 해당 제안: 입사일 +1년 ≤ 퇴사일 → O, 아니면 X (저장 전 회색 표시용)
export const severanceSuggest = (p: Position): "O" | "X" | null => {
    if (!p.startDate || !p.endDate) return null;
    const [y, m, d] = p.startDate.split("-");
    const oneYear = `${+y + 1}-${m}-${d}`;
    return p.endDate >= oneYear ? "O" : "X";
};

export const isOnboardingComplete = (p: Position, siblings: Position[]): boolean => {
    const ob = p.onboarding || {};
    if (!p.partId || !p.startDate) return false;
    if (!p.employmentInsurance) return false;
    if (p.employmentInsurance === "O" && acqMode(p, siblings) === "input") {
        if (!ob.acquisitionInput || !ob.acquisitionDone) return false;
    }
    if (p.videoConsentStatus !== "agreed" && p.videoConsentStatus !== "disagreed") return false;
    if (p.isFirst) return false;                       //첫 계약 서명완료 (2회차 미서명이 있어도 유지)
    if (!ob.kakaoSent || !ob.kakaoRead) return false;
    if (!p.sexOffender || !p.sexOffender.checkedAt) return false;
    return true;
};

export const isOffboardingComplete = (p: Position, siblings: Position[]): boolean => {
    const off = p.offboarding || {};
    if (!p.endDate) return false;
    //상실신고: 고보 O이고 다른 재직 파트가 없을 때만 필수 (겸직은 입력 가능하되 선택)
    if (p.employmentInsurance === "O" && lossMode(p, siblings) === "input" && !hasOtherActive(siblings)) {
        if (!off.lossInput || !off.lossDone) return false;
    }
    if (!off.salarySettled || !off.salaryTransferred) return false;
    const sev = off.severanceEligible ? String(off.severanceEligible.value || "") : "";
    if (sev !== "O" && sev !== "X") return false;
    if (sev === "O" && (!off.severanceSettled || !off.severanceTransferred)) return false;
    if (!off.released) return false;
    return true;
};

//공통 오류 토스트
export const toastResult = (result: any, okText: string | null, failText: string): boolean => {
    if (result && result.message === "success") {
        if (okText) toast.success(okText, toastOptions);
        return true;
    }
    const map: any = { wrongDate: "날짜가 올바르지 않습니다", wrongPart: "파트 값이 올바르지 않습니다", wrongValue: "값이 올바르지 않습니다", alreadyReleased: "이미 해제된 직무입니다", noEndDate: "퇴사일을 먼저 입력하세요", cancelFailed: "미서명 계약서 취소에 실패했습니다" };
    toast.error(map[result && result.message] || errorText(result, failText), toastOptions);
    return false;
};
